import {PlusIcon} from "@heroicons/react/24/outline";
import {format} from "date-fns";

import {
  BillingUserType,
  BuyOrderType,
  EventType,
  LocalitySelectedType,
  OrderType,
  ProductType,
  SeatType,
} from "data/types";
import {FC, useEffect, useState} from "react";
import NcModal from "shared/NcModal/NcModal";
import {
  useDeleteBillingMutation,
  useGetBillingsQuery,
  useLazyGetCustomerListSubscriberIdQuery,
  useSaveOrderTemporaryMutation,
} from "store/api";
import AddBilling from "./AddBilling";
import BillingCard from "./BillingCard";
import ButtonCircle from "shared/Button/ButtonCircle";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import AddUserSubscriber from "./Subscriber/AddUserSubscriber";
import {Nuvei} from "components/Nuvei/Nuvei";
import {Payphone} from "components/Payphone/Payphone";

const Billing: FC<{
  localitiesSelected: LocalitySelectedType[];
  setLocalitiesSelected: React.Dispatch<
    React.SetStateAction<LocalitySelectedType[]>
  >;
  isSubscriber: boolean;
  paymentSelected: string;
  totalToPay: number;
  eventName: string;
  valueIva: number;
  haveIva: boolean;
  codeDiscountQueue: string;
  event: EventType;
  codeQueue: string;
  orderSubtotal: number;
  orderComision: number;
  orderComisionIva: number;
  orderIva: number;
  orderTotal: number;
  orderDiscount: number;
  influencer: string;
  verifyStock: () => Promise<string[]>;
}> = ({
  localitiesSelected,
  setLocalitiesSelected,
  isSubscriber,
  paymentSelected,
  totalToPay,
  eventName,
  valueIva,
  haveIva,
  verifyStock,
  codeDiscountQueue,
  event,
  codeQueue,
  orderSubtotal,
  orderComision,
  orderComisionIva,
  orderIva,
  orderTotal,
  orderDiscount,
  influencer,
}) => {
  const {data, refetch} = useGetBillingsQuery({});
  const [billings, setBillings] = useState<BillingUserType[]>([]);
  const [deleteBilling] = useDeleteBillingMutation();
  const [isOpened, setIsOpened] = useState(false);
  const [isOpenedCustomer, setIsOpenedCustomer] = useState<{
    [key: string]: boolean;
  }>({});
  const [selected, setSelected] = useState<BillingUserType | null>(null);
  const [subscribers, setSubscribers] = useState<
    Record<string, {name: string; identification: string; id: string}>
  >({});

  const [errorList, setErrorList] = useState<any[]>([]);

  const [alert, setAlert] = useState<string | null>(null);
  const totalQuantity = localitiesSelected
    .filter((loc) => loc.quantity > 0)
    .reduce((sum, loc) => sum + loc.quantity, 0);
  const description = `Compra Boletos BEE(web) ${totalQuantity} - ${eventName}`;

  const [getCustomerListSubscriberId] =
    useLazyGetCustomerListSubscriberIdQuery();

  const [buyOrder, setBuyOrder] = useState<BuyOrderType>();

  const [saveOrderTemporary] = useSaveOrderTemporaryMutation();

  const transformBillings = (rawData: any[]): BillingUserType[] => {
    return rawData.map((billing: BillingUserType) => ({
      address: billing.address,
      documentType: billing.documentType,
      email: billing.email,
      identification: billing.identification,
      isDeleted: billing.isDeleted,
      name: billing.name,
      phone: billing.phone,
      prefix: billing.prefix,
      _id: billing._id,
    }));
  };

  const updateIdentification = (index: string, identification: string) => {
    setSubscribers((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        identification,
      },
    }));
  };

  const isFullNameInSubscribers = () => {
    const values = Object.values(subscribers);
    const totalQuantity = localitiesSelected
      .filter((loc) => loc.quantity > 0)
      .reduce((sum, loc) => sum + loc.quantity, 0);

    const allNamesFilled = values.every(
      (subscriber) => subscriber.name && subscriber.name !== "",
    );

    return allNamesFilled && values.length === totalQuantity;
  };

  useEffect(() => {
    if (data) setBillings(transformBillings(data.data));
  }, [data]);

  const handleRefresh = async () => {
    const result = await refetch();
    if (result.data) setBillings(transformBillings(result.data.data));
  };

  const handleDelete = async (id: string) => {
    const {data} = await deleteBilling(id);
    if (data.meta.status === 200) {
      setBillings((prev) => prev.filter((b) => b._id !== id));
    }
  };

  const searchUser = async (
    index: string,
    listSubscriberId: string,
    localityId: string,
    zoneId: string,
    customerIndex: number,
  ) => {
    const word = subscribers[index]?.identification;

    if (!word) return;

    const isDuplicate = Object.entries(subscribers).some(
      ([key, value]) => key !== index && value.identification === word,
    );

    if (isDuplicate) {
      setAlert("Cada abono debe tener un número de identificación diferente.");
      setTimeout(() => setAlert(null), 3000);
      setSubscribers((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          name: "",
          id: "",
        },
      }));
      return;
    }
    const {data, error} = await getCustomerListSubscriberId({
      word,
      listSubscriberId,
    });
    if (error) {
      const fetchError = error as {
        status: number;
        data: {err: any; statusMessage: string};
      };
      if (fetchError.status === 409) {
        setAlert(
          "No se encontró ningún usuario con ese número de identificación.",
        );
        setIsOpenedCustomer((prev) => ({...prev, [index]: true}));
        // openmodal

        setSubscribers((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            name: "",
            id: "",
          },
        }));
        setTimeout(() => setAlert(null), 3000);
        return;
      } else if (fetchError.status === 410) {
        setAlert(
          word +
            " ya es un abonado en este evento en la localidad " +
            fetchError.data.err.locality,
        );
        setTimeout(() => setAlert(null), 3000);
      }
    }
    if (data && data.meta.status === 200) {
      const isDuplicated = Object.entries(subscribers).some(([key, value]) => {
        if (key === index) return false;
        return (
          value.identification === data.customer.email ||
          value.identification === data.customer.username ||
          value.identification === data.customer.ci
        );
      });

      if (isDuplicated) {
        setAlert(
          "Ya se encuentra asignado un abono a este usuario en otro campo.",
        );
        setSubscribers((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            name: "",
            id: "",
          },
        }));
        setTimeout(() => setAlert(null), 3000);
        return;
      }
      if (zoneId) {
        setLocalitiesSelected((prev) =>
          prev.map((locality) => {
            if (locality._id !== localityId) return locality; // solo modificamos la localidad deseada

            return {
              ...locality,
              zones: locality.zones.map((zone) => {
                if (zone._id !== zoneId) return zone;
                return {
                  ...zone,
                  customers:
                    zone.customers?.map((c, idx) =>
                      idx === customerIndex ? data.customer._id : c,
                    ) || [],
                };
              }),
            };
          }),
        );
      }
      setSubscribers((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          name: data.customer.name,
          id: data.customer._id,
        },
      }));
    }
  };

  const fillOrder = async () => {
    let statusModal = false;
    const now = new Date();
    const formattedDate = format(now, "yyyy/MM/dd-HH:mm:ss");
    const userIdRef = JSON.parse(localStorage.getItem("user")!)?._id;
    const devReference =
      paymentSelected === "Nuvei"
        ? `EVT-W ${userIdRef}-${formattedDate}`
        : `EVT-W-P.${userIdRef}-${formattedDate}`;
    const order: OrderType = {
      codeDiscountQueue: codeDiscountQueue,
      vatTax: haveIva,
      category: event.category,
      event: event._id,
      codeQueue: codeQueue,
      quantity: totalQuantity,
      orderSubtotal: orderSubtotal,
      orderComision: orderComision,
      orderComisionIva: orderComisionIva,
      orderIva: orderIva,
      orderTotal: orderTotal,
      statusBuy: false,
      platformBuy: "WEB_BEE",
      paymentType:
        paymentSelected === "Nuvei"
          ? "PAYMENTEZ"
          : paymentSelected.toUpperCase(),
      orderDiscount: orderDiscount,
      orderTotalPartial: orderDiscount > 0 ? orderTotal + orderDiscount : 0,
      orderType: isSubscriber ? "SUBSCRIBER" : "NORMAL",
      invoiceUser: selected!._id!,
      ...(influencer !== "" && {
        influencer: influencer,
      }),
      // next fields are filled after payment
      devReference: devReference,
      authorizationCode: "",
      transactionId: "",
      endTime: event.endTime,
    };

    const products: ProductType[] = localitiesSelected
      .filter((locality) => locality.quantity > 0)
      .map((loc) => {
        const seats: SeatType[] = loc.haveSeatSelection
          ? loc.zones
              .filter((zone) => zone.seats.length > 0)
              .map((zone) => {
                return zone.seats.map((seat, index) => {
                  return {
                    position: seat,
                    number: zone.numbers[index],
                    topic: zone._id,
                    ...(isSubscriber &&
                      loc.haveSeatSelection &&
                      zone.customers && {
                        customer: zone.customers[index],
                      }),
                  };
                });
              })
              .flat()
          : Array.from({length: loc.quantity}, () => ({
              position: "0_0",
              number: "0_0",
            }));

        if (isSubscriber && !loc.haveSeatSelection) {
          seats.forEach((seat, index) => {
            const subscriberKey = loc._id + loc.haveDiscount + index;
            seat.customer = subscribers[subscriberKey]?.id || "";
          });
        }

        return {
          locality: loc._id,
          localityName: loc.description,
          localityPrice: loc.price,
          secuenceStart: loc.secuenceStart,
          acronym: loc.acronym,
          endTime: loc.endTime,
          quantity: loc.quantity,
          acronymEvent: event.acronym,
          phase: loc.phase,
          haveSpecialDiscount: loc.haveDiscount,
          valueSpecialDiscount: loc.haveDiscount ? loc.price : 0,
          listSubscriber: loc.listSubscriber,
          seat: seats,
        };
      });

    if (paymentSelected === "Nuvei") {
      statusModal = await saveOrderTemporaryNuvei({order, products});
    } else {
      statusModal = true;
    }

    setBuyOrder({order, products});
    return statusModal;
  };

  const saveOrderTemporaryNuvei = async (buyOrder: BuyOrderType) => {
    const body = {
      typeBuy: buyOrder.order.orderType,
      dev_reference: buyOrder.order.devReference,
      details: buyOrder,
    };
    const {data, error} = await saveOrderTemporary(body);
    if (error) {
      return false;
    }
    if (data && data.meta.status === 201) {
      return true;
    }
    return false;
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6 text-neutral-800 dark:text-neutral-100">
        Mis Datos de Facturación
      </h2>

      <div className="grid gap-4 sm:grid-cols-3">
        {billings.length > 0 ? (
          billings.map((billing) => (
            <BillingCard
              key={billing._id}
              selected={selected}
              setSelected={setSelected}
              billing={billing}
              handleDelete={handleDelete}
            />
          ))
        ) : (
          <p className="text-neutral-600 dark:text-neutral-400 col-span-2">
            No hay datos de facturación disponibles.
          </p>
        )}

        {billings.length < 3 && (
          <NcModal
            isOpenProp={isOpened}
            onCloseModal={() => setIsOpened(false)}
            renderTrigger={() => (
              <div className="w-full h-full">
                <button
                  className="w-full h-full rounded-2xl border border-neutral-200 dark:border-neutral-700 shadow-md p-5 bg-white dark:bg-neutral-900 flex flex-col items-center justify-center transition hover:shadow-lg min-h-[160px]"
                  onClick={() => setIsOpened(true)}
                >
                  <PlusIcon className="w-10 h-10 mb-2" />
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Agregar nuevo
                  </span>
                </button>
              </div>
            )}
            contentExtraClass="w-full sm:w-2/3"
            renderContent={() => (
              <AddBilling refresh={handleRefresh} setIsOpened={setIsOpened} />
            )}
            modalTitle="Agregar nuevos datos de facturación"
            showCloseButton={true}
          />
        )}
      </div>
      {
        <div className="mt-6">
          {isSubscriber && (
            <h2 className=" text-xl font-bold mb-6 text-neutral-800 dark:text-neutral-100">
              Datos de abonados
            </h2>
          )}
          {isSubscriber && (
            <span className="text-sm text-gray-500 dark:text-gray-400 mb-4 block">
              Cada abono debe ser obligatoriamente asociado a un número de
              identificación diferente.
            </span>
          )}
          {localitiesSelected
            .filter((locality) => locality.quantity > 0)
            .map((locality) => {
              const allSeatLabels = locality.zones.flatMap(
                (zone) => zone.numbers,
              );
              const allZoneIds = locality.zones.flatMap((zone) =>
                zone.numbers.map(() => zone._id),
              );
              const allCustomerIndexes = locality.zones.flatMap((zone) =>
                zone.numbers.map((_, idx) => idx),
              );

              return (
                <div
                  key={locality._id + locality.haveDiscount}
                  className="mb-4 p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg"
                >
                  <h3 className="text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-100">
                    {locality.description} X {locality.quantity}
                  </h3>

                  {isSubscriber && (
                    <div className="w-full space-y-3">
                      {Array.from({length: locality.quantity}).map(
                        (_, index) => {
                          const seatLabel = allSeatLabels[index];
                          const zoneId = allZoneIds[index];
                          const customerIndex = allCustomerIndexes[index];

                          return (
                            <div
                              key={locality._id + locality.haveDiscount + index}
                              className="flex items-center space-x-3 w-full"
                            >
                              <i className="las la-user flex-shrink-0 text-lg"></i>
                              {locality.haveSeatSelection && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {seatLabel}
                                </span>
                              )}

                              <input
                                type="text"
                                placeholder="# Identificación, Username o Email"
                                inputMode="text"
                                value={
                                  subscribers[
                                    locality._id + locality.haveDiscount + index
                                  ]?.identification || ""
                                }
                                onChange={(e) =>
                                  setSubscribers((prev) => ({
                                    ...prev,
                                    [locality._id +
                                    locality.haveDiscount +
                                    index]: {
                                      ...prev[
                                        locality._id +
                                          locality.haveDiscount +
                                          index
                                      ],
                                      identification: e.target.value,
                                    },
                                  }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    searchUser(
                                      locality._id +
                                        locality.haveDiscount +
                                        index,
                                      locality.listSubscriber[0]?._id,
                                      locality._id,
                                      zoneId || "",
                                      customerIndex,
                                    );
                                  }
                                }}
                              />
                              <NcModal
                                key={
                                  locality._id +
                                  locality.haveDiscount +
                                  index +
                                  "mod"
                                }
                                modalTitle="Asignar abono a usuario"
                                isOpenProp={
                                  isOpenedCustomer[
                                    locality._id + locality.haveDiscount + index
                                  ]
                                }
                                onCloseModal={() =>
                                  setIsOpenedCustomer((prev) => ({
                                    ...prev,
                                    [locality._id +
                                    locality.haveDiscount +
                                    index]: false,
                                  }))
                                }
                                contentExtraClass="w-full sm:w-2/3"
                                closeOnClickOutside={false}
                                renderTrigger={() => (
                                  <ButtonCircle
                                    onClick={() =>
                                      searchUser(
                                        locality._id +
                                          locality.haveDiscount +
                                          index,
                                        locality.listSubscriber[0]?._id,
                                        locality._id,
                                        zoneId || "",
                                        customerIndex,
                                      )
                                    }
                                  >
                                    <i className="las la-search text-lg"></i>
                                  </ButtonCircle>
                                )}
                                renderContent={() => (
                                  <AddUserSubscriber
                                    key={
                                      locality._id +
                                      locality.haveDiscount +
                                      index +
                                      "add"
                                    }
                                    setIsOpened={setIsOpenedCustomer}
                                    refresh={searchUser}
                                    index={
                                      locality._id +
                                      locality.haveDiscount +
                                      index
                                    }
                                    listSubscriberId={
                                      locality.listSubscriber[0]?._id
                                    }
                                    ci={
                                      subscribers[
                                        locality._id +
                                          locality.haveDiscount +
                                          index
                                      ]?.identification || ""
                                    }
                                    onChange={updateIdentification}
                                  />
                                )}
                                showCloseButton={true}
                              />

                              <span className="w-1/3 text-xs text-gray-500 dark:text-gray-400 truncate">
                                {
                                  subscribers[
                                    locality._id + locality.haveDiscount + index
                                  ]?.name
                                }
                              </span>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      }
      <div className="flex justify-center w-full">
        <NcModal
          renderTrigger={(openModal) => (
            <ButtonPrimary
              className="mt-4"
              disabled={
                isSubscriber
                  ? !(selected && isFullNameInSubscribers())
                  : !selected
              }
              onClick={async () => {
                //
                const errors = await verifyStock();
                if (errors.length > 0) {
                  setErrorList([...errors]);
                  setTimeout(() => setErrorList([]), 5000);
                  return;
                }

                const state = await fillOrder();
                if (state) return openModal();
              }}
            >
              Pagar ${totalToPay.toFixed(2)}
            </ButtonPrimary>
          )}
          renderContent={() =>
            paymentSelected === "Nuvei" ? (
              <Nuvei
                amount={totalToPay}
                description={description}
                valueIva={valueIva}
                haveIva={haveIva}
                buyOrder={buyOrder!}
                // setShowLoader={() => {}}
              />
            ) : (
              <Payphone
                amount={Number(totalToPay.toFixed(2))}
                description="Event"
                valueIva={valueIva}
                haveIva={haveIva}
                buyOrder={buyOrder!}
                // setShowLoader={() => {}}
              />
            )
          }
          modalTitle="Proceder al pago"
          contentExtraClass="w-full sm:w-2/3"
          showCloseButton={true}
          closeOnClickOutside={false}
        />
      </div>
      {alert && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex items-center gap-4">
          {alert}
        </div>
      )}
      {errorList.length > 0 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex flex-col gap-2">
          <span>Debes seleccionar:</span>
          {errorList.map((error: string, index) => (
            <div key={index}>{error}</div> // Cada error en una nueva línea
          ))}
        </div>
      )}
    </div>
  );
};

export default Billing;
